import { captureMicrofrontendError } from "./captureMicrofrontendError"
import type { MfeProps, MicroFrontendConfig, MicroFrontendContentProps } from "./interfaces"

declare global {
  interface Window {
    renderMFE?: (props: MfeProps) => unknown
    removeMFE?: () => unknown
    [key: `render-mfe-${string}`]: ((props: MfeProps) => unknown) | undefined
    [key: `remove-mfe-${string}`]: (() => unknown) | undefined
  }
}

const createScript = ({
  id,
  type = "module",
  crossOrigin = "",
  defer = false,
  src,
  onload
}: {
  id: string
  type?: string
  crossOrigin?: string
  defer?: boolean
  src: string
  onload?: () => void
  onerror?: (event: string | Event) => void
}): HTMLScriptElement => {
  const script = document.createElement("script")
  script.id = id
  script.type = type
  script.crossOrigin = crossOrigin
  script.defer = defer
  script.src = src
  if (onload) {
    script.onload = onload
  }
  if (onerror) {
    script.onerror = onerror
  }

  return script
}

function buildStyle({ href }: { href: string }): HTMLElement {
  const link = document.createElement("link")
  link.type = "text/css"
  link.rel = "stylesheet"
  link.href = href

  return link
}

const baseUrl = (url: string): string => {
  const parsedUrl = new URL(url)

  const portPart = parsedUrl.port !== "" ? `:${parsedUrl.port}` : ""

  return `${parsedUrl.protocol}//${parsedUrl.hostname}${portPart}`
}

export const mfeConfig = ({ importUrl, webComponentName }: MicroFrontendContentProps): MicroFrontendConfig => {
  const scriptId = `${webComponentName}-mfe-script` as const
  const containerId = `${webComponentName}-container`
  return {
    manifest: importUrl,
    container: containerId,
    scriptId,
    loaded: document.getElementById(scriptId) !== null,
    renderMfe(props: MfeProps) {
      const key: `render-mfe-${string}` = `render-mfe-${webComponentName}`
      window[key] ??= window.renderMFE
      const fn = window[key]

      if (!fn) return

      try {
        fn(props)
      } catch (error) {
        captureMicrofrontendError(error, {
          mfeName: webComponentName,
          customerId: props.mfeConfig.customerId,
          errorType: "mfe_render_error",
          context: {
            container_id: containerId,
            mfe_props: props
          }
        })
        throw error // Re-throw so error boundary can handle it
      }
    },
    removeMfe() {
      const key: `remove-mfe-${string}` = `remove-mfe-${webComponentName}`
      window[key] ??= window.removeMFE
      const fn = window[key]
      if (fn) fn()
    },
    appendDocumentMfe({ src, css, props }: { src: string; css?: string[]; props: MfeProps }) {
      if (css) {
        css.forEach((style: string) => {
          document.head.appendChild(buildStyle({ href: `${baseUrl(importUrl)}${style}` }))
        })
      }

      const script = createScript({
        id: scriptId,
        src: `${baseUrl(importUrl)}${src}`,
        onload: () => {
          this.renderMfe(props)
        },
        onerror: (event) => {
          // Capture script loading errors
          captureMicrofrontendError(new Error("Failed to load microfrontend script"), {
            mfeName: webComponentName,
            errorType: "mfe_script_load_error",
            context: {
              script_src: `${baseUrl(importUrl)}${src}`,
              container_id: containerId,
              script_error: event
            }
          })
        },
        type: "module",
        defer: true
      })

      document.head.appendChild(script)
    }
  }
}
